import React, { useCallback, useMemo, useReducer, useRef, useEffect } from 'react';
import PropTypes from "prop-types";
import styled from "styled-components";

import InputField from "./InputField";
import NumPad from "./NumPad";
import UnitTag from "./UnitTag";
import UnitSelector from "./UnitSelector";
import UpDownArrows from "./UpDownArrows";

import useProfile from "../../hooks/useProfile";
import { useSnapshot } from "valtio";
import useKeyboardFocus from "../../hooks/useKeyboardFocus";

import {
  addThousandSeparator,
  removeThousandSeparator,
  formatDecimalSeparator,
  formatNumber,
} from "../../utils/decimalSeparator";

const units = ['Pcs', 'Hours', "Boxes"];
const currencies = ['USD', 'DKK', 'EUR'];

const NumericInputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const initialState = {
  stringValue: '',
  showNumpad: false,
  showUnitSelector: false,
};

function reducer(state, action) {
  switch (action.type) {
    case 'SET_STRING_VALUE':
      return { ...state, stringValue: action.payload };
    case 'TOGGLE_NUMPAD':
      return { ...state, showNumpad: !state.showNumpad };
    case 'TOGGLE_UNIT_SELECTOR':
      return { ...state, showUnitSelector: !state.showUnitSelector };
    default:
      return state;
  }
}

const NumericInput = React.memo(({
  $variant,
  name,
  inputId,
  unit,
  value,
  decimals = 0,
  placeholder,
  setValue,
  isRed,
  allowNegative = true,
  onBlur,
  autoFocus = false,
  testId
}) => {
  const { pState } = useProfile();
  const profile = useSnapshot(pState);
  const decimalSeparator = profile.decimalSeparator;

  const inputRef = useRef(null);
  const isFocused = useKeyboardFocus();

  useEffect(() => {
    if (autoFocus) {
      inputRef.current?.focus()
      // Selected once the value is in (it loads in an effect after this one),
      // so what is typed replaces it rather than following it.
      requestAnimationFrame(() => inputRef.current?.select())
    }
  }, [])
  const formatTimeoutRef = useRef(null);
  const isBlurringRef = useRef(false);

  const [state, dispatch] = useReducer(reducer, initialState);
  const { stringValue, showNumpad, showUnitSelector } = state;

  const variantConfig = useMemo(() => {
    switch ($variant) {
      case 'amount':
        return { tag: currencies, minDec: 2, maxDec: 2, inputMode: 'decimal' };
      case 'price':
        return { tag: currencies, minDec: 2, maxDec: Math.max(2, decimals), inputMode: 'decimal' };
      case 'quantity':
        return { tag: units, minDec: 0, maxDec: Math.max(0, decimals), inputMode: 'numeric' };
      default:
        return { tag: null, minDec: 0, maxDec: decimals, inputMode: 'decimal' };
    }
  }, [$variant, decimals]);

  const loadValues = useCallback(() => {
    let initialValue = formatNumber(value, variantConfig.minDec, variantConfig.maxDec);
    initialValue = formatDecimalSeparator(initialValue, decimalSeparator);
    initialValue = addThousandSeparator(initialValue, decimalSeparator);
    dispatch({ type: 'SET_STRING_VALUE', payload: initialValue });
  }, [value, variantConfig, decimalSeparator]);

  const saveValue = useCallback((strValue) => {
    const cleanValue = removeThousandSeparator(strValue, decimalSeparator);
    // Convert decimal separator to standard dot for parseFloat
    const normalizedValue = decimalSeparator === "," ? cleanValue.replace(",", ".") : cleanValue;
    const nValue = parseFloat(normalizedValue);
    if (!isNaN(nValue)) {
      if (allowNegative || nValue >= 0) {
        setValue({ target: { name, value: nValue } });
      } else {
        setValue({ target: { name, value: 0 } });
      }
    } else if (strValue === '-' && allowNegative) {
      dispatch({ type: 'SET_STRING_VALUE', payload: '-' });
    } else {
      setValue({ target: { name, value: null } });
    }
  }, [name, setValue, decimalSeparator, allowNegative]);

  useEffect(() => {
    if (value !== undefined && decimalSeparator && !isFocused) {
      loadValues();
    }
  }, [value, decimalSeparator, isFocused, loadValues]);

  const handleUnitSelect = useCallback((selectedUnit) => {
    dispatch({ type: 'TOGGLE_UNIT_SELECTOR' });
    // Handle the unit change here
  }, []);

  const handleArrowClick = useCallback((amount) => {
    const oldValue = removeThousandSeparator(stringValue, decimalSeparator);
    // Convert decimal separator to standard dot for parseFloat
    const normalizedOldValue = decimalSeparator === "," ? oldValue.replace(",", ".") : oldValue;
    const parsedOldValue = parseFloat(normalizedOldValue);
    let newValue = (isNaN(parsedOldValue) ? 0 : parsedOldValue) + amount;
    if (!allowNegative) {
      newValue = Math.max(0, newValue);
    }
    let formattedValue = formatNumber(newValue, variantConfig.minDec, variantConfig.maxDec);
    // Convert decimal separator if needed (formatNumber always returns with dot)
    if (decimalSeparator === "," && formattedValue.includes(".")) {
      formattedValue = formattedValue.replace(".", ",");
    }
    saveValue(formattedValue);
    dispatch({ type: 'SET_STRING_VALUE', payload: formattedValue });
  }, [stringValue, decimalSeparator, variantConfig, saveValue, allowNegative]);

  const formatInput = useCallback((value) => {
    // Important: strip thousand separators before parsing.
    // Otherwise e.g. "10.000,00" (DK format) becomes "10.000.00" when we replace "," -> ".",
    // and parseFloat() will interpret that as 10.
    const cleanValue = removeThousandSeparator(value, decimalSeparator);

    // Convert decimal separator to standard dot for parseFloat
    const normalizedValue = decimalSeparator === "," ? cleanValue.replace(",", ".") : cleanValue;
    const nValue = parseFloat(normalizedValue);

    if (!isNaN(nValue)) {
      const validValue = allowNegative ? nValue : Math.max(0, nValue);
      let numString = formatNumber(validValue, variantConfig.minDec, variantConfig.maxDec);

      // Convert decimal separator if needed (formatNumber always returns with dot)
      if (decimalSeparator === "," && numString.includes(".")) {
        numString = numString.replace(".", ",");
      }

      numString = addThousandSeparator(numString, decimalSeparator);
      dispatch({ type: 'SET_STRING_VALUE', payload: numString });
      saveValue(numString);
    } else if (value === '-' && allowNegative) {
      dispatch({ type: 'SET_STRING_VALUE', payload: '-' });
    } else {
      dispatch({ type: 'SET_STRING_VALUE', payload: '' });
      saveValue('');
    }
  }, [variantConfig, decimalSeparator, allowNegative, saveValue]);

  const handleInputBlur = useCallback((e) => {
    isBlurringRef.current = true;

    if (formatTimeoutRef.current) {
      clearTimeout(formatTimeoutRef.current);
      formatTimeoutRef.current = null;
    }

    formatInput(e.target.value);
    isBlurringRef.current = false;
    onBlur?.();
  }, [formatInput, onBlur]);

  const handleInputChange = useCallback((e) => {
    const newValue = e.target.value;

    // Allow more lenient input during typing
    const sanitizedValue = newValue.replace(
      new RegExp(`[^0-9${decimalSeparator}${allowNegative ? '-' : ''}]`, 'g'),
      ''
    );

    dispatch({ type: 'SET_STRING_VALUE', payload: sanitizedValue });

    // Clear any existing timeout (we no longer debounce-save)
    if (formatTimeoutRef.current) {
      clearTimeout(formatTimeoutRef.current);
      formatTimeoutRef.current = null;
    }

    // Save while typing, but avoid committing "incomplete" numbers like "2500,".
    // This prevents partial values racing the blur commit.
    if (sanitizedValue === '' || (allowNegative && sanitizedValue === '-')) return;
    if (decimalSeparator && sanitizedValue.endsWith(decimalSeparator)) return;

    saveValue(sanitizedValue);
  }, [decimalSeparator, allowNegative, saveValue]);

  const handleFocus = useCallback(() => {
    const oldValue = removeThousandSeparator(stringValue, decimalSeparator);
    dispatch({ type: 'SET_STRING_VALUE', payload: oldValue });
  }, [stringValue, decimalSeparator]);

  // Clear the format timeout on unmount
  useEffect(() => {
    return () => {
      if (formatTimeoutRef.current) {
        clearTimeout(formatTimeoutRef.current);
      }
    };
  }, []);

  return (
    <NumericInputWrapper>
      <InputField
        name={name}
        inputId={inputId}
        inputRef={inputRef}
        value={stringValue}
        placeholder={placeholder}
        onInputChange={handleInputChange}
        onInputBlur={handleInputBlur}
        handleArrowClick={handleArrowClick}
        onFocus={handleFocus}
        isReadOnly={false}
        isRed={isRed}
        inputMode={variantConfig.inputMode}
        aria-label={`${name} input`}
        testId={testId}
      />
      {!isRed && (
        <UpDownArrows
          onIncrement={() => handleArrowClick(1)}
          onDecrement={() => handleArrowClick(-1)}
        />
      )}
      {showNumpad && (
        <NumPad
          decimalSeparator={decimalSeparator || '.'}
          onKeyPress={(key) => dispatch({ type: 'SET_STRING_VALUE', payload: stringValue + key })}
          onClose={() => dispatch({ type: 'TOGGLE_NUMPAD' })}
          allowNegative={allowNegative}
        />
      )}
      {unit && (
        <UnitTag
          unit={unit}
          units={variantConfig.tag}
          onChange={handleUnitSelect}
        />
      )}
      {showUnitSelector && (
        <UnitSelector
          units={variantConfig.tag}
          selectedUnit={unit}
          onSelect={handleUnitSelect}
        />
      )}
    </NumericInputWrapper>
  );
});

NumericInput.propTypes = {
  name: PropTypes.string.isRequired,
  $variant: PropTypes.oneOf(['amount', 'price', 'quantity']),
  unit: PropTypes.string,
  value: PropTypes.number,
  decimals: PropTypes.number,
  placeholder: PropTypes.string,
  setValue: PropTypes.func.isRequired,
  isRed: PropTypes.bool,
  allowNegative: PropTypes.bool,
};

export default NumericInput;
